import queryString from "query-string";
import { handleSessionStorage } from "utils/storage";

// Comera Explore embeds the storefront in a WebView and appends a signed JWT to
// the URL. The guide documents the param as `auth_token`, the live integration
// sends `comera_token` -- we accept either so a rename on their side cannot
// silently disable SSO.
export const TOKEN_PARAMS = ["comera_token", "auth_token"];
export const TRANSACTION_ID_PARAM = "comera_transaction_id";

export const TOKEN_STORAGE_KEY = "comeraToken";
export const TRANSACTION_ID_STORAGE_KEY = "comeraTransactionId";

// Values arrive URL-encoded and have been observed wrapped in a stray quote
// (`...BX0%22`), which decodes to a trailing `"`.
const sanitize = (value) => {
  if (typeof value !== "string") return null;
  const cleaned = value.trim().replace(/^["']+|["']+$/g, "");
  return cleaned.length ? cleaned : null;
};

export const readComeraParams = () => {
  const params = queryString.parse(window?.location?.search || "");
  const paramName = TOKEN_PARAMS.find((key) => sanitize(params?.[key]));

  return {
    token: paramName ? sanitize(params[paramName]) : null,
    transactionId: sanitize(params?.[TRANSACTION_ID_PARAM]),
    paramName: paramName || null,
  };
};

export const getComeraToken = () =>
  sanitize(handleSessionStorage.getItem(TOKEN_STORAGE_KEY));

export const getComeraTransactionId = () =>
  sanitize(handleSessionStorage.getItem(TRANSACTION_ID_STORAGE_KEY));

export const persistComeraParams = ({ token, transactionId }) => {
  if (token) handleSessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  if (transactionId)
    handleSessionStorage.setItem(TRANSACTION_ID_STORAGE_KEY, transactionId);
};

export const clearComeraToken = () =>
  handleSessionStorage.removeItem(TOKEN_STORAGE_KEY);

// The JWT identifies a customer, so it must not linger in the address bar where
// browser history, referrer headers and Sentry would pick it up.
export const stripTokenFromUrl = () => {
  try {
    if (!window?.history?.replaceState) return;
    const url = new URL(window.location.href);
    let mutated = false;
    TOKEN_PARAMS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        mutated = true;
      }
    });
    if (mutated) window.history.replaceState({}, document.title, url.toString());
  } catch (error) {
    // A non-fatal hygiene step -- never block the boot sequence on it.
  }
};

// Sent alongside the OTP so the backend can re-verify the signature and bind the
// Comera uuid to the user it just verified. We forward the token rather than a
// decoded uuid: a uuid the client asserts is not proof of anything.
export const getComeraLinkFields = () => {
  const comeraToken = getComeraToken();
  if (!comeraToken) return {};

  const comeraTransactionId = getComeraTransactionId();
  return comeraTransactionId
    ? { comeraToken, comeraTransactionId }
    : { comeraToken };
};

export const getComeraOrderFields = () => {
  const comeraTransactionId = getComeraTransactionId();
  return comeraTransactionId ? { comeraTransactionId } : {};
};

// A 401 wipes sessionStorage twice -- once in the axios interceptor and again in
// the root reducer's `logout` handler -- then reloads. Capturing before and
// restoring after lets the reload re-exchange the token instead of dropping the
// customer onto the OTP screen inside the Comera app. An explicit logout does
// not restore, so signing out stays meaningful.
export const captureComeraSession = () => ({
  token: getComeraToken(),
  transactionId: getComeraTransactionId(),
});

export const restoreComeraSession = (snapshot) => {
  if (!snapshot?.token && !snapshot?.transactionId) return;
  persistComeraParams({
    token: snapshot?.token,
    transactionId: snapshot?.transactionId,
  });
};
